import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
  Output,
  EventEmitter,
} from "@angular/core";
import { DeviceDetectorService } from "ngx-device-detector";
import { BreakpointService } from "@bitwarden/web-vault/app/shared/breakpoints";
import { I18nService } from "@bitwarden/common/abstractions/i18n.service";
import { OnBackDirective } from "@bitwarden/web-vault/app/directives/onBack.directive";
import { ActionButton } from "@bitwarden/web-vault/app/components/buttons/gloss-button/actionButton";

@Component({
  selector: "app-details-wrapper",
  templateUrl: "./details-wrapper.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DetailsWrapperComponent extends OnBackDirective {
  @Input() title: string = "";
  @Input() subtitle: string = "";
  @Input() description: string = "";
  @Input() showExpandIcon: boolean = false;
  @Input() headerIconName: string = "right-arrow";
  @Input() headerIconSize: number = 5;
  @Input() headerIconWrapperClass: string = "";
  @Input() backButtonClass: string = "btn-icon";
  @Input() icon: string = "";
  /** Set false to suppress the built-in desktop header (icon/title/edit button) so a
   *  consumer can render its own instead. Mobile's modal-header is unaffected. */
  @Input() showDesktopHeader: boolean = true;
  @Input() showDeleteButton: boolean = false;
  @Input() showEditIcon: boolean = true;
  /** Opt-in override for the header title's font-size classes (mobile modal-header). Empty = default. */
  @Input() titleClass: string = "";
  @Input() headerIconClickable: boolean = false;
  @Input() isOverlay = false;
  /** Force mobile (stacked) layout regardless of device type or breakpoint — e.g. when the
   *  panel is displayed as a full-screen overlay at a custom breakpoint wider than the LG breakpoint. */
  @Input() forceSmallScreen: boolean = false;
  @Output() onEditClicked = new EventEmitter<void>();
  @Output() onDeleteClicked = new EventEmitter<void>();
  @Output() onHeaderIconClicked = new EventEmitter<void>();
  protected deviceService: DeviceDetectorService = inject(DeviceDetectorService);
  private i18n: I18nService = inject(I18nService);
  private isLgOrSmaller = inject(BreakpointService).isLgOrSmaller;
  protected get showMobileLayout(): boolean {
    return (
      this.forceSmallScreen ||
      this.deviceService.isMobile() ||
      (this.isOverlay && this.isLgOrSmaller())
    );
  }

  private readonly pillClass =
    "tw-flex tw-items-center tw-justify-center tw-gap-2 tw-h-10 tw-px-4 tw-rounded-full " +
    "tw-bg-dt-surface-elevated tw-border tw-border-solid tw-border-color-border-subtle";

  protected editButtonOptions = new ActionButton({
    text: this.i18n.t("edit"),
    icon: "add-entry",
    iconClass: "tw-h-4 tw-w-4 tw-text-primary",
    fullClass: this.pillClass,
    onClick: () => this.onEditClicked.emit(),
  });

  protected deleteButtonOptions = new ActionButton({
    text: this.i18n.t("delete"),
    icon: "trash-bin",
    iconClass: "tw-h-4 tw-w-4 tw-text-danger",
    fullClass: this.pillClass,
    onClick: () => this.onDeleteClicked.emit(),
  });
}
